import { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, Loader, X } from 'lucide-react'
import { listDataSources, listFields, fetchMetadata, createField, updateField, deleteField } from './calculatedFieldsApi'
import { getFormulaError, slugify } from './formulaSupport'
import FormulaEditor from './FormulaEditor'
import type { CalcField, DataSourceSummary, SourceMetadata, FieldType, CatalogField } from './types'

const FORMATS = ['number', 'currency', 'percent', 'text'] as const

interface Props {
  projectId: number
  initialSourceId?: number
}

interface ModalState {
  mode: 'create' | 'edit'
  field?: CalcField
  name: string
  fieldId: string
  fieldIdTouched: boolean
  formula: string
  fieldType: FieldType
  format: string
  error: string | null
}

export default function CalculatedFieldsManager({ projectId, initialSourceId }: Props) {
  const [sources, setSources] = useState<DataSourceSummary[]>([])
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null)
  const [fields, setFields] = useState<CalcField[]>([])
  const [metadata, setMetadata] = useState<SourceMetadata>({ metrics: [], dimensions: [] })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // La metadata (champs natifs) est indispensable au blocage des collisions de fieldId
  // à la création : sans elle, `nativeKeys` serait vide et un champ calculé pourrait
  // masquer un champ natif. Tant qu'elle n'a pas chargé, la création est désactivée.
  const [metadataReady, setMetadataReady] = useState(false)
  const [modal, setModal] = useState<ModalState | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listDataSources(projectId)
      .then((list) => {
        if (cancelled) return
        setSources(list)
        const preselected = initialSourceId && list.some((s) => s.id === initialSourceId)
          ? initialSourceId
          : (list.length > 0 ? list[0].id : null)
        setSelectedSourceId(preselected)
        if (list.length === 0) setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err.message)
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [projectId, initialSourceId])

  const selectedSource = sources.find((s) => s.id === selectedSourceId) || null

  const refetchFields = useCallback(() => {
    if (!selectedSourceId) return Promise.resolve()
    return listFields(projectId, selectedSourceId).then(setFields)
  }, [projectId, selectedSourceId])

  useEffect(() => {
    if (!selectedSource) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setMetadataReady(false)
    Promise.all([
      listFields(projectId, selectedSource.id),
      fetchMetadata(selectedSource.type),
    ])
      .then(([fieldsData, metadataData]) => {
        if (cancelled) return
        setFields(fieldsData)
        setMetadata(metadataData)
        setMetadataReady(true)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedSource?.id])

  const catalogFields: CatalogField[] = [...metadata.dimensions, ...metadata.metrics]

  function openCreateModal() {
    setModal({
      mode: 'create',
      name: '',
      fieldId: '',
      fieldIdTouched: false,
      formula: '',
      fieldType: 'metric',
      format: 'number',
      error: null,
    })
  }

  function openEditModal(field: CalcField) {
    setModal({
      mode: 'edit',
      field,
      name: field.name,
      fieldId: field.fieldId,
      fieldIdTouched: true,
      formula: field.formula,
      fieldType: field.fieldType,
      format: field.format,
      error: null,
    })
  }

  function closeModal() {
    setModal(null)
  }

  function updateModal(patch: Partial<ModalState>) {
    setModal((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  function handleNameChange(value: string) {
    if (!modal) return
    if (!modal.fieldIdTouched) {
      updateModal({ name: value, fieldId: slugify(value) })
    } else {
      updateModal({ name: value })
    }
  }

  function handleFieldIdChange(value: string) {
    updateModal({ fieldId: value, fieldIdTouched: true })
  }

  function handleTypeChange(fieldType: FieldType) {
    updateModal({ fieldType, format: fieldType === 'metric' ? 'number' : 'text' })
  }

  async function handleSubmit() {
    if (!modal || !selectedSourceId) return

    if (!modal.name.trim()) {
      updateModal({ error: 'Nom requis' })
      return
    }

    const formulaError = getFormulaError(modal.formula)
    if (!modal.formula.trim() || formulaError) {
      updateModal({ error: formulaError ? formulaError.message : 'Formule invalide' })
      return
    }

    if (modal.mode === 'create') {
      const nativeKeys = new Set([...metadata.metrics, ...metadata.dimensions].map((f) => f.key))
      const calcKeys = new Set(fields.map((f) => f.fieldId))
      if (nativeKeys.has(modal.fieldId) || calcKeys.has(modal.fieldId)) {
        updateModal({ error: 'Identifiant déjà utilisé sur cette source' })
        return
      }
    }

    try {
      if (modal.mode === 'create') {
        await createField({
          projectId,
          dataSourceId: selectedSourceId,
          name: modal.name,
          fieldId: modal.fieldId,
          formula: modal.formula,
          fieldType: modal.fieldType,
          format: modal.format,
        })
      } else if (modal.field) {
        await updateField(modal.field.id, {
          projectId,
          name: modal.name,
          formula: modal.formula,
          fieldType: modal.fieldType,
          format: modal.format,
        })
      }
      closeModal()
      await refetchFields()
    } catch (err) {
      updateModal({ error: (err as Error).message })
    }
  }

  async function handleDelete(id: number) {
    await deleteField(id, projectId)
    setConfirmDeleteId(null)
    await refetchFields()
  }

  return (
    <div>
      {sources.length > 1 && (
        <div className="mb-4">
          <label htmlFor="calc-fields-source" className="block text-xs font-medium text-[#8B8FA7] mb-1">
            Source
          </label>
          <select
            id="calc-fields-source"
            value={selectedSourceId ?? ''}
            onChange={(e) => setSelectedSourceId(Number(e.target.value))}
            className="rounded-xl border border-[#E2E4ED] bg-[#F4F5FA] px-3 py-2 text-sm text-[#1B2559]"
          >
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.type.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#1B2559]">Champs calculés</h3>
        <button
          type="button"
          onClick={openCreateModal}
          disabled={!metadataReady}
          title={!metadataReady ? 'Champs de la source indisponibles' : undefined}
          className="flex items-center gap-1.5 rounded-xl bg-[#6366F1] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.25} />
          Créer
        </button>
      </div>

      {loadError && <p className="text-xs text-red-500 mb-2">{loadError}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[#8B8FA7]">
          <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.25} />
          Chargement…
        </div>
      ) : fields.length === 0 ? (
        <p className="text-xs text-[#8B8FA7]">Aucun champ calculé</p>
      ) : (
        <ul className="space-y-1.5">
          {fields.map((field) => (
            <li
              key={field.id}
              className="relative flex items-center justify-between rounded-xl border border-[#E2E4ED] px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#1B2559]">{field.name}</span>
                <span className="rounded-full bg-[#F4F5FA] px-2 py-0.5 text-[10px] font-medium text-[#8B8FA7]">
                  {field.fieldType === 'dimension' ? 'dimension' : 'métrique'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={`Modifier ${field.name}`}
                  onClick={() => openEditModal(field)}
                  className="text-[#8B8FA7] hover:text-[#1B2559]"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.25} />
                </button>
                <button
                  type="button"
                  aria-label={`Supprimer ${field.name}`}
                  onClick={() => setConfirmDeleteId(field.id)}
                  className="text-[#8B8FA7] hover:text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} />
                </button>
              </div>

              {confirmDeleteId === field.id && (
                <div className="absolute mt-8 flex items-center gap-2 rounded-xl border border-[#E2E4ED] bg-white p-2 shadow-lg">
                  <span className="text-xs text-[#1B2559]">Supprimer « {field.name} » ?</span>
                  <button
                    type="button"
                    onClick={() => handleDelete(field.id)}
                    className="rounded-lg bg-red-500 px-2 py-1 text-xs font-medium text-white"
                  >
                    Confirmer
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded-lg border border-[#E2E4ED] px-2 py-1 text-xs text-[#8B8FA7]"
                  >
                    Annuler
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-[#1B2559]">
                {modal.mode === 'create' ? 'Créer un champ calculé' : 'Modifier le champ calculé'}
              </h4>
              <button type="button" onClick={closeModal} className="text-[#8B8FA7] hover:text-[#1B2559]">
                <X className="w-4 h-4" strokeWidth={1.25} />
              </button>
            </div>

            {modal.error && <p className="mb-3 text-xs text-red-500">{modal.error}</p>}

            <div className="space-y-3">
              <div>
                <label htmlFor="calc-field-name" className="block text-xs font-medium text-[#8B8FA7] mb-1">
                  Nom
                </label>
                <input
                  id="calc-field-name"
                  type="text"
                  value={modal.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full rounded-xl border border-[#E2E4ED] bg-[#F4F5FA] px-3 py-2 text-sm text-[#1B2559]"
                />
              </div>

              <div>
                <label htmlFor="calc-field-id" className="block text-xs font-medium text-[#8B8FA7] mb-1">
                  Identifiant
                </label>
                <input
                  id="calc-field-id"
                  type="text"
                  value={modal.fieldId}
                  onChange={(e) => handleFieldIdChange(e.target.value)}
                  readOnly={modal.mode === 'edit'}
                  className={`w-full rounded-xl border border-[#E2E4ED] bg-[#F4F5FA] px-3 py-2 text-sm text-[#1B2559] ${modal.mode === 'edit' ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="calc-field-type" className="block text-xs font-medium text-[#8B8FA7] mb-1">
                    Type
                  </label>
                  <select
                    id="calc-field-type"
                    value={modal.fieldType}
                    onChange={(e) => handleTypeChange(e.target.value as FieldType)}
                    className="w-full rounded-xl border border-[#E2E4ED] bg-[#F4F5FA] px-3 py-2 text-sm text-[#1B2559]"
                  >
                    <option value="metric">Métrique</option>
                    <option value="dimension">Dimension</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="calc-field-format" className="block text-xs font-medium text-[#8B8FA7] mb-1">
                    Format
                  </label>
                  <select
                    id="calc-field-format"
                    value={modal.format}
                    onChange={(e) => updateModal({ format: e.target.value })}
                    className="w-full rounded-xl border border-[#E2E4ED] bg-[#F4F5FA] px-3 py-2 text-sm text-[#1B2559]"
                  >
                    {FORMATS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#8B8FA7] mb-1">Formule</label>
                <FormulaEditor
                  value={modal.formula}
                  onChange={(v) => updateModal({ formula: v })}
                  fields={catalogFields}
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-xl border border-[#E2E4ED] px-3 py-1.5 text-xs font-medium text-[#8B8FA7]"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="rounded-xl bg-[#6366F1] px-3 py-1.5 text-xs font-medium text-white"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
